/**
 * I18nProvider — wraps the React tree with i18next context (Story 1893).
 *
 * Initializes i18next on first render and provides the I18nextProvider
 * to all child components. Renders a Suspense boundary for lazy-loaded
 * translation files.
 *
 * @module shared/i18n/I18nProvider
 */

import React, { Suspense, useRef } from 'react';
import { I18nextProvider } from 'react-i18next';
import { initI18n, i18next } from './i18n-config';

interface I18nProviderProps {
  children: React.ReactNode;
  /** Base path for loading translation files via HTTP backend. */
  localesBasePath?: string;
}

/** Visible loading indicator shown while i18n translations are being loaded. */
const I18N_LOADING_FALLBACK = (
  <div
    role="status"
    aria-label="Loading"
    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}
  >
    Loading…
  </div>
);

/**
 * Wraps child components with i18next context.
 *
 * Place this as high as possible in the component tree — typically
 * above ErrorBoundary and outside BrowserRouter.
 */
export function I18nProvider({
  children,
  localesBasePath,
}: I18nProviderProps): React.JSX.Element {
  const initialized = useRef(false);

  if (!initialized.current) {
    initI18n(localesBasePath);
    initialized.current = true;
  }

  return (
    <I18nextProvider i18n={i18next}>
      <Suspense fallback={I18N_LOADING_FALLBACK}>{children}</Suspense>
    </I18nextProvider>
  );
}
